import { useFetchLocation } from "@/common/queries/use-fetch-location"
import { useQuery } from "@tanstack/react-query"

import type { HomeScreenRecommendedAddress } from "@be-miniapp/api"
import { v1GetHomeRecommendedAddress } from "@be-miniapp/api"
import { MapPinIcon } from "@be-miniapp/ui/components/ui/icons/map-pin"
import { Skeleton } from "@be-miniapp/ui/components/ui/skeleton"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"

import { useClientInfo } from "@/store/client-info"
import { useRideDataStore } from "@/store/ride-data"
import type { RideLocation } from "@/store/ride-data"
import { useUserStore } from "@/store/user-session"
import { fromGeocodeLocationToStandardLocation } from "@/utils/geocode"

export const RecommendedAddressHomeScreen = () => {
  const currentAddress = useUserStore((state) => state.currentAddress)

  const locationSource = useClientInfo((state) => state.locationSource)
  const sessionLocation = useUserStore((state) => state.location)
  const { data: recommendedAddress, isLoading } = useQuery({
    queryKey: [
      "recommended-address",
      locationSource,
      sessionLocation.latitude,
      sessionLocation.longitude,
    ],
    queryFn: async () => {
      const resp = await v1GetHomeRecommendedAddress()
      return resp.data
    },
    enabled: locationSource !== "fallback",
    staleTime: 60 * 60 * 1000,
    refetchInterval: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    retry: 3,
  })

  const { fetch: fetchLocationBeforeRide } = useFetchLocation()

  const setPickup = useRideDataStore((state) => state.setPickup)
  const setDropoff = useRideDataStore((state) => state.setDropoff)

  const navigate = useNavigate()

  const handleAddressClick = async (
    address: HomeScreenRecommendedAddress["recommend_addresses"][number],
    addrName: string
  ) => {
    const locData = await fetchLocationBeforeRide()

    setTimeout(() => {
      const currentLocation = locData || currentAddress

      if (!currentLocation) {
        return
      }

      const addrData = fromGeocodeLocationToStandardLocation(currentLocation)

      const pickup = {
        type: "pick-up",
        ...addrData,
        isConfirmed: false,
      } satisfies RideLocation

      setPickup(pickup)

      setDropoff({
        id: address.id ? String(address.id) : undefined,
        label: address.type || undefined,
        name: addrName,
        address: address.addr,
        type: "drop-off",
        placeId: address.type + address.id,
        location: {
          latitude: address.lat,
          longitude: address.lng,
        },
        isConfirmed: true,
        metadata: "suggestion",
      })

      navigate("/ride/confirm-address", {
        state: {
          pickup: pickup,
        },
        animate: true,
      })
    }, 100)
  }

  return (recommendedAddress?.recommend_addresses ?? []).length > 0 ? (
    <div className="no-scrollbar flex max-w-full gap-3 overflow-x-auto border-be-neutral-25 border-t px-3 py-3">
      {isLoading &&
        Array.from({ length: 3 }).map((_, idx) => (
          <Skeleton
            key={idx}
            className="flex h-7 shrink-0 cursor-pointer items-center gap-2 truncate rounded-full bg-gray-300/50 px-3 text-gray-700 text-sm"
            style={{ width: Math.floor(Math.random() * 100) + 80 }}
          >
            <MapPinIcon
              width={10}
              height={16}
              className="shrink-0"
              fill="#1A6CEA"
            />
          </Skeleton>
        ))}

      {recommendedAddress?.recommend_addresses.map((address) => {
        const addrName = address.type
          ? address.type
          : address.addr.split(",")[0]
        return (
          <div
            key={address.type + address.id}
            className="flex h-7 max-w-[152px] shrink-0 cursor-pointer items-center gap-2 truncate rounded-full bg-be-zalo-chip-bg/70 px-3 text-be-text text-sm"
            onClick={() => handleAddressClick(address, addrName)}
          >
            <MapPinIcon
              width={10}
              height={16}
              className="shrink-0"
              fill="#1A6CEA"
            />
            <span className="truncate font-normal text-[0.9rem] leading-5">
              {addrName}
            </span>
          </div>
        )
      })}
    </div>
  ) : null
}
